import IConfigService from 'core/service/config/IConfigService';
import { iDef } from 'core/util/TypeUtils';
import { Registry } from "react-registry";
import { v4 as generateUuid } from "uuid";

import ActionTypes from 'core/actions/actionTypes';
import { beginAjaxCall } from 'core/actions/ajaxStatusActions';
import initialState from 'core/reducers/initialState';
import IRemoteConditionService from 'core/service/remoteCondition/IRemoteConditionService';
import AppToaster from 'core/util/AppToaster';
import Class from 'core/util/Class';
import { PropertyProvider } from 'core/util/property/PropertyProvider';
import merge from "lodash/merge";
import StartupException from "../util/exception/StartupException";
import IPropertyRecord from "../util/property/IPropertyRecord";
import IPropertyReference from "../util/property/IPropertyReference";
import ServiceRegistry from '../util/ServiceRegistry';
import { editChangesIncrement, editSetActive } from './editActions';
import { userSetCondition } from './userActions';

export function loadConfigSuccess(config, key?: string) {
	return { type: ActionTypes.CONFIG_LOAD_SUCCESS, config, key };
}


export function loadContextPropertiesSuccess(properties, key?: string) {
	return { type: ActionTypes.CONFIG_CONTEXT_PROPERTIES_SUCCESS, properties, key };
}


export function saveConfigSuccess(key?: string) {
	return (dispatch) => {
		dispatch({ type: ActionTypes.CONFIG_SAVE_SUCCESS});
		dispatch(loadConfig(key));
	};
}

export const loadConfig = (key?: string) => {
	return (dispatch, getState) => {
		dispatch(beginAjaxCall());

		const configService = ServiceRegistry.get(IConfigService);
		const k = key || getState().config.key;

		return configService.getConfig(k).then((response: any) => {
			return JSON.parse(response);
		}).then((response) => {
			return JSON.parse(response.data.config);
		}).then((data: any) => {

			// merge with inital state incase any new fields were added
			const merged = merge(data, initialState.config);

			dispatch(loadConfigSuccess(merged, k));
		}).catch((error) => {
			// Fatal, kick the user back to the gateway for a new token
			throw new StartupException("Failed to fetch config", error);
		});
	};
};


export const loadContextProperties = (key?: string) => {
	return (dispatch, getState) => {
		dispatch(beginAjaxCall());

		const configService = ServiceRegistry.get(IConfigService);
		const k = key || getState().config.key;

		return configService.getContextProperties(k).then((response: any) => {
			return JSON.parse(response);
		}).then((response) => {
			return JSON.parse(response.data.contextProperties);
		}).then((data: any) => {
			dispatch(loadContextPropertiesSuccess(data, k));
		}).catch((error) => {
			// Fatal, kick the user back to the gateway for a new token
			throw new StartupException("Failed to fetch Context Properties", error);
		});
	};
};

export const saveConfig = () => {
	return (dispatch, getState) => {
		dispatch(beginAjaxCall());

		const config = getState().config;
		const configService = ServiceRegistry.get(IConfigService);

		return configService.saveConfig(config, config.key).then(() => {
			dispatch(saveConfigSuccess(config.key));
		}).catch((error) => {
			throw (error);
		});
	};
};

export const configViewAdd = (viewId: string, instance: string, config: object) => {
	return (dispatch) => {

		return dispatch(editChangesIncrement()).then(() => {
			dispatch({ type: ActionTypes.CONFIG_VIEW_ADD, viewId, instance, config});
			return Promise.resolve(viewId);
		});
	};
};

export const configViewRemove = (viewId: string, instance: string) => {
	return (dispatch) => {

		return dispatch(editChangesIncrement()).then(() => {
			dispatch({ type: ActionTypes.CONFIG_VIEW_REMOVE, viewId, instance });
			return Promise.resolve();
		});
	};
};

export const configViewClone = (viewId: string, variant: string) => {
	return (dispatch) => {

		return dispatch(editChangesIncrement()).then(() => {
			dispatch({ type: ActionTypes.CONFIG_VIEW_CLONE, viewId, variant});
			return Promise.resolve(viewId);
		});
	};
};

export const configPanelAdd = (instance: string, config: object) => {
	return (dispatch) => {

		return dispatch(editChangesIncrement()).then(() => {
			dispatch({ type: ActionTypes.CONFIG_PANEL_ADD, instance, config});
			return Promise.resolve(instance);
		});
	};
};

export const configPanelViewAdd = (instance: string, viewInstance: string) => {
	return (dispatch) => {

		return dispatch(editChangesIncrement()).then(() => {
			dispatch({ type: ActionTypes.CONFIG_PANEL_VIEW_ADD, instance, viewInstance });
			return Promise.resolve(instance);
		});
	};
};

export const configPanelViewRemove = (instance: string) => {
	return (dispatch) => {
		return dispatch(editChangesIncrement()).then(() => {
			dispatch({ type: ActionTypes.CONFIG_PANEL_VIEW_REMOVE, instance });
			return Promise.resolve();
		});
	};
};


export const configPanelCreate = (id: string, initialConfig?: object, callback?: (instance: string) => void) => {
	return (dispatch) => {
		// get a new UUIDv4
		const uuid = generateUuid();

		// get the default config for the panel
		const ConfigClass = Registry.get({id, registry: "panels:config"}) as Class<any>;

		// make a config with the default and set the id

		let addResult;

		if(ConfigClass) {
			const config = {...(new ConfigClass()), ...initialConfig, id};
			// dispatch event to add config to store
			addResult = dispatch(configPanelAdd(uuid, config));
		} else {
			addResult = dispatch(configPanelAdd(uuid, {...initialConfig, id}));
		}

		// trigger callback with result if present
		addResult.then((instance: string) => {
			if(iDef(callback)) { callback(instance); }
		});
	};
};

export const configPanelRemove = (instance: string) => {
	return (dispatch) => {
		dispatch(editChangesIncrement());
		dispatch({ type: ActionTypes.CONFIG_PANEL_REMOVE, instance});
		return Promise.resolve();
	};
};

export const configPanelMove = (panelInstance: string, targetInstance) => {
	return (dispatch) => {
		dispatch(editChangesIncrement());
		dispatch({ type: ActionTypes.LAYOUT_PANEL_MOVE, panelInstance, targetInstance });
	};
};

export const configPanelSort = (targetInstance, dragIndex, hoverIndex) => {
	return (dispatch) => {
		dispatch(editChangesIncrement());
		dispatch({ type: ActionTypes.LAYOUT_PANEL_SORT, targetInstance, dragIndex, hoverIndex });
	};
};

export const configPanelBorderToggle = (targetInstance, enabled, title) => {
	return (dispatch) => {
		dispatch(editChangesIncrement());
		dispatch({ type: ActionTypes.CONFIG_PANEL_BORDER_TOGGLE, targetInstance, enabled, title });
	};
};


export const configPropertyAdd = (reference: IPropertyReference, record: IPropertyRecord<any>) => {
	return (dispatch) => {
		dispatch(editChangesIncrement());
		dispatch({
			type: ActionTypes.CONFIG_PROPERTY_ADD,
			reference,
			record: PropertyProvider.setAsRecord(record)
		});
	};
};

export const configConditionRemove = (uuid: string) => {
	return (dispatch) => {
		dispatch(editChangesIncrement());
		dispatch({ type: ActionTypes.CONFIG_CONDITION_REMOVE, uuid });
	};
};

export const configConditionAdd = (uuid: string, externalId: string) => {
	return (dispatch) => {
		return ServiceRegistry.get(IRemoteConditionService).evaluateConditions([externalId])
			.then((ditions) => {
				const c = ditions.find((dition) => dition.externalId === externalId);
				if (c) {
					dispatch({ type: ActionTypes.CONFIG_CONDITION_ADD, uuid, externalId });
					dispatch(userSetCondition(externalId, !!c.value));
				}
			});
	};
};



// WEBPACK FOOTER //
// ./src/core/actions/configActions.ts